import { useCallback, useEffect, useRef, useState } from 'react';
import { timesheetApi } from '@/services/timesheetApi';
import type { CostCentre, Project, Timesheet } from '@/types/timesheet';
import type { EditedEntries } from './useTimesheetReviewEdit';

interface UseProjectCostCentresParams {
  screenMode: 'demo' | 'live';
  viewingTimesheet: Timesheet | null;
  editedEntries: EditedEntries;
  effectiveProjects: Project[];
  /** Changes whenever the queue's project data is refetched, invalidating the cache. */
  projectDataEpoch: number;
}

/**
 * Cost centres valid for a project, keyed by project code. The queue-wide cost
 * centre list is not project specific, so the edit-mode selector needs the
 * per-project set (fetched for the entries of the open review dialog).
 */
export function useProjectCostCentres({
  screenMode,
  viewingTimesheet,
  editedEntries,
  effectiveProjects,
  projectDataEpoch,
}: UseProjectCostCentresParams): Record<string, CostCentre[]> {
  const [costCentresByProjectCode, setCostCentresByProjectCode] = useState<Record<string, CostCentre[]>>({});
  const fetchedCostCentreCodesRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    setCostCentresByProjectCode({});
    fetchedCostCentreCodesRef.current = {};
  }, [projectDataEpoch]);

  const fetchCostCentresForProject = useCallback(async (projectCode: string) => {
    if (fetchedCostCentreCodesRef.current[projectCode]) return;
    fetchedCostCentreCodesRef.current[projectCode] = true;
    try {
      const costCentres = await timesheetApi.getCostCentresByProjectCode(projectCode);
      setCostCentresByProjectCode(prev => ({ ...prev, [projectCode]: costCentres }));
    } catch (error) {
      console.error(`Failed to fetch cost centres for project ${projectCode}:`, error);
      fetchedCostCentreCodesRef.current[projectCode] = false;
    }
  }, []);

  // Warm the per-project cost centre cache for every project referenced by the
  // open review dialog, so the selector is usable as soon as edit mode starts.
  useEffect(() => {
    if (screenMode !== 'live' || !viewingTimesheet) return;
    const projectCodes = new Set<string>();
    viewingTimesheet.entries.forEach(entry => {
      const projectId = editedEntries[entry.id]?.projectId ?? entry.projectId;
      if (!projectId) return;
      const code = effectiveProjects.find(p => p.id === projectId)?.code
        ?? (projectId === entry.projectId ? entry.projectCode : undefined);
      if (code) projectCodes.add(code);
    });
    projectCodes.forEach(code => fetchCostCentresForProject(code));
  }, [screenMode, viewingTimesheet, editedEntries, effectiveProjects, fetchCostCentresForProject]);

  return costCentresByProjectCode;
}
